import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { Subject, takeUntil, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BackOffice24UsersEditComponent } from './back-office24-users-edit/back-office24-users-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { Subscription } from 'rxjs';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { BackOffice24AddOrganizationComponent } from './back-office24-add-organization/back-office24-add-organization.component';
import { BackOffice24OrgMembersComponent } from './back-office24-org-members/back-office24-org-members.component';
import { BackOffice24UsersAddComponent } from './back-office24-users-add/back-office24-users-add.component';
import { MyConfigService } from '../../../../services/my-config.service';
import moment from 'moment';

@Component({
  selector: 'app-back-office24-users',
  templateUrl: './back-office24-users.component.html',
  styleUrl: './back-office24-users.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BackOffice24UsersComponent {
  @Input() organization: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();
  organizations: any[] = [];
  selectedOrg: any = null;
  loading: boolean = false;
  showEditMemberTypesModal: boolean = false;
  componentDestroyed$: Subject<boolean> = new Subject();
  organizationId: any;
  membersList: any;
  hasParkingOperatorRole: any = false;
  filteredOrgUsers: any[] = [];
  searchQuery: string = '';
  selectedMembershipTypes: any[] = [];
  subscriptionParking: any[] = [];
  gatesParking: any[] = [];
  maintenanceParking: any[] = [];

  // Used by many of the modals in this module
  organizationMemberTypes = [
    { id: 1, name: 'Parkifly Administrator', value: 'ParkiflyAdministrator', translation: "USER_MANAGEMENT.MEMBER_TYPES.PARKIFLY_ADMIN" },
    { id: 2, name: 'Subscription', value: 'Subscription', translation: "USER_MANAGEMENT.MEMBER_TYPES.SUBSCRIPTION" },
    { id: 32, name: 'Maintenance', value: 'Maintenance', translation: "USER_MANAGEMENT.MEMBER_TYPES.MAINTENANCE" },
    { id: 64, name: 'Vehicle Administrator', value: 'VehicleAdministrator', translation: "USER_MANAGEMENT.MEMBER_TYPES.VEHICLE_ADMINISTRATOR" },
    { id: 128, name: 'User Administrator', value: 'UserAdministrator', translation: "USER_MANAGEMENT.MEMBER_TYPES.USER_ADMINISTRATOR" },
    { id: 256, name: 'Parking Operator', value: 'ParkingOperator', translation: "USER_MANAGEMENT.MEMBER_TYPES.PARKING_OPERATOR" },
    { id: 512, name: 'Parking Administrator', value: 'ParkingAdministrator', translation: "USER_MANAGEMENT.MEMBER_TYPES.PARKING_ADMINISTRATOR" },
    { id: 1024, name: 'Shuttle Driver', value: 'ShuttleDriver', translation: "USER_MANAGEMENT.MEMBER_TYPES.SHUTTLE_DRIVER" },
    { id: 2048, name: 'Parking Surveillance', value: 'ParkingSurveillance', translation: "USER_MANAGEMENT.MEMBER_TYPES.PARKING_SURVEILLANCE" },
    { id: 4096, name: 'Driver', value: 'Driver', translation: "USER_MANAGEMENT.MEMBER_TYPES.DRIVER" },
    { id: 8192, name: 'Staff', value: 'Staff', translation: "USER_MANAGEMENT.MEMBER_TYPES.STAFF" },
    { id: 16384, name: 'Dashboard', value: 'Dashboard', translation: "USER_MANAGEMENT.MEMBER_TYPES.DASHBOARD" }
  ];

  NotUsedMemberTypes = [
    { id: 0, name: 'None', value: 'None' },
    { id: 4, name: 'Not Used 3', value: 'NotUsed3' },
    { id: 8, name: 'Not Used 4', value: 'NotUsed4' },
    { id: 16, name: 'Not Used 5', value: 'NotUsed5' },
    { id: 32, name: 'Not Used 6', value: 'NotUsed6' },
  ]

  parkingMemberTypes = [
    { id: 2, name: 'Subscription', value: 'Subscription', translation: "USER_MANAGEMENT.MEMBER_TYPES.SUBSCRIPTION" },
    { id: 32, name: 'Maintenance', value: 'Maintenance', translation: "USER_MANAGEMENT.MEMBER_TYPES.MAINTENANCE" },
    { id: 64, name: 'Vehicle Administrator', value: 'VehicleAdministrator', translation: "USER_MANAGEMENT.MEMBER_TYPES.VEHICLE_ADMINISTRATOR" },
    { id: 128, name: 'User Administrator', value: 'UserAdministrator', translation: "USER_MANAGEMENT.MEMBER_TYPES.USER_ADMINISTRATOR" },
  ];

  parkingAccessLevel = {
    None: 0,
    Subscription: 1,
    Vehicle: 2,
    Maintenance: 4
  }

  parkingData: any[] = [];
  originalParkingData: any[] = [];
  isEditing: boolean = false;
  hasChanges: boolean = false;

  constructor(
    private backOfficeService: BackOfficeService,
    private dialog: MatDialog,
    private backOffice24StatesService: BackOffice24StatesService,
    private myConfigService: MyConfigService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => {
        this.organization = org
        this.parkingData = org?.config?.parkings;
      }
    );
    this.hasParkingOperatorRole = this.myConfigService.hasOrganizationIdRole(this.organization?.organizationId, 'ParkingOperator', this.organization?.organizationParentId);
    this.getOrganizations();
  }

  setAdminParkingAccess(org: any) {
    this.parkingData.forEach((parking: any) => {
      const access = org?.parkingAccess?.find((access: any) => access.parkingId === parking.parkingId)
      if (access) {
        parking.accessLevel = access.accessLevel;
        parking.access = {
          subscription: (access.accessLevel & this.parkingAccessLevel.Subscription) === this.parkingAccessLevel.Subscription,
          vehicle: (access.accessLevel & this.parkingAccessLevel.Vehicle) === this.parkingAccessLevel.Vehicle,
          maintenance: (access.accessLevel & this.parkingAccessLevel.Maintenance) === this.parkingAccessLevel.Maintenance
        }
        parking.parkingAccessId = access?.id;
      } else {
        parking.accessLevel = 0;
        parking.access = {
          subscription: false,
          vehicle: false,
          maintenance: false
        }
        parking.parkingAccessId = null;
      }
    });
  }

  ngOnDestroy() {
    this.orgSubscription.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  addNewOrganization() {
    const dialogRef = this.dialog.open(BackOffice24AddOrganizationComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw',
      width: '800px',
      data: { organizationId: this.organizationId },
    });

    dialogRef.componentInstance.saveChangesEvent.subscribe((org) => {
      const data = org.organization
      const obj = {
        id: data.id,
        name: data.name,
        type: data.type,
        members: data?.members || [],
        organizations: data?.organizations || []
      }
      this.organizations.push(obj);
      this.selectOrganization(this.organizations[0]);
      this.setAdminParkingAccess(this.organizations[0]);
    });
  }

  getOrganizations() {
    this.loading = true;
    return this.backOfficeService.getOrganizations(this.organization.organizationId || 'a848ccd3-fd8d-4035-925e-571ebb4e93e8').pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          const allMembers = [...(data?.members || [])];

          data?.organizations?.forEach((org: any) => {
            if (org.members) {
              allMembers.push(...org.members);
            }
          });

          const uniqueMembers = allMembers.filter((member, index, self) =>
            index === self.findIndex(m => m.userId === member.userId)
          );

          this.membersList = uniqueMembers;
          this.organizationId = data.id;

          data?.members?.forEach((member: any) => {
            this.setMemberTypeNamesArray(member);
          });
          // console.log(member);

          const obj = {
            id: data.id,
            name: data.name,
            type: data.type,
            parentId: data.parentId,
            shuttleBookingAllowedUntil: data.shuttleBookingAllowedUntil,
            members: data?.members || []
          }
          data.organizations.unshift(obj);
          this.organizations = data?.organizations || [];
          this.organizations.forEach((org: any) => {
            if (org.shuttleBookingAllowedUntil) {
              org.shuttleBookingAllowedUntil = moment(org.shuttleBookingAllowedUntil);
            }
          });
          this.loading = false;
          this.selectOrganization(this.organizations[0]);
        },
        error: (error) => {
          this.loading = false;
        }
      });
  }

  filterUsers(): void {
    console.log("searching");
    let filtered = [...this.selectedOrg.members];

    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(user =>
        (user.firstName + " " + user.lastName).toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.memberTypeNamesArray.some((type: any) => type.value.toLowerCase().includes(query))
      );
    }

    if (this.selectedMembershipTypes.length > 0) {
      filtered = filtered.filter(user =>
        this.selectedMembershipTypes.some((type: any) => user.memberTypeNamesArray.some((t: any) => t.value === type))
      );
    }

    console.log(filtered);
    this.filteredOrgUsers = filtered;
  }

  onMembershipTypeChange(selectedTypesEvent: any) {
    this.selectedMembershipTypes = selectedTypesEvent.value;
    console.log(selectedTypesEvent.value);
    this.filterUsers();
  }


  selectOrganization(org: any) {
    this.selectedOrg = org;
    console.log(org);
    this.setAdminParkingAccess(org);
    org?.members?.forEach((member: any) => {
      this.setMemberTypeNamesArray(member);
      // if (typeof member.memberTypeNames === 'string') {
      // } else if (!Array.isArray(member.memberTypeNames)) {
      //   member.memberTypeNames = [];
      // }
    });
    this.filteredOrgUsers = org.members;
  }

  onEditMemberTypes(user: any) {
    const dialogRef = this.dialog.open(BackOffice24UsersEditComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw',
      width: '800px',
      data: {
        user: user,
        organizationId: this.organizationId,
        targetOrganizationId: this.selectedOrg.id,
        organization: this.organization,
        organizationMemberTypes: this.organizationMemberTypes,
        orgType: this.selectedOrg.type
      },
    });

    dialogRef.componentInstance.saveChangesEvent.subscribe((res) => {
      if (res && res.data) {
        const memberIndex = this.selectedOrg.members.findIndex((member: any) => member.userId === user.userId);
        if (memberIndex !== -1) {
          this.selectedOrg.members[memberIndex] = res.data;
          this.selectedOrg.members[memberIndex].memberType = res.data.memberType;
          this.selectedOrg.members[memberIndex].memberTypeNames = res.data.memberTypeNames;

          if (typeof res.data.memberTypeNames === 'string') {
            this.setMemberTypeNamesArray(res.data);
          }
        }
      }
      this.filterUsers();
    });

    dialogRef.componentInstance.deleteEvent.subscribe((res) => {
      if (res && res.data) {
        const memberIndex = this.selectedOrg.members.findIndex((member: any) => member.userId === user.userId);
        if (memberIndex !== -1) {
          this.selectedOrg.members.splice(memberIndex, 1);
        }
      }
      this.filterUsers();
    });
  }

  addNewUser() {
    const dialogRef = this.dialog.open(BackOffice24UsersAddComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw',
      width: '800px',
      data: {
        targetOrganizationId: this.selectedOrg.id,
        organizationId: this.organization?.organizationId,
        organizationMemberTypes: this.organizationMemberTypes,
        parkingMemberTypes: this.parkingMemberTypes,
        orgType: this.selectedOrg.type
      },
    });

    dialogRef.componentInstance.saveChangesEvent.subscribe((result) => {
      if (result && result.userAdded) {
        this.getOrganizations();
      }
    });
  }

  addExistingUser() {
    const availableMembers = this.membersList.filter((member: any) => {
      const isAlreadyMember = this.selectedOrg?.members?.some((orgMember: any) => orgMember.userId === member.userId);
      return !isAlreadyMember;
    });

    const dialogRef = this.dialog.open(BackOffice24OrgMembersComponent, {
      maxHeight: '95vh',
      maxWidth: '95vw',
      width: '800px',
      data: {
        members: availableMembers,
        organizationId: this.organization.organizationId,
        targetOrganizationId: this.selectedOrg?.id,
        organizationMemberTypes: this.organizationMemberTypes,
        parkingMemberTypes: this.parkingMemberTypes,
        orgType: this.selectedOrg.type
      },
    });

    dialogRef.componentInstance.saveChangesEvent.subscribe((user) => {

      if (user && user.userAdded && user.data) {
        this.setMemberTypeNamesArray(user.data);
        this.selectedOrg.members = [...this.selectedOrg.members, user.data];
      }

      this.filterUsers();
    });
  }

  setMemberTypeNamesArray(member: any) {
    member.memberTypeNamesArray = member.memberTypeNames.split(',').map((type: string) => type.trim());
    member.memberTypeNamesArray = member.memberTypeNamesArray.map((type: any) => {
      const typeValue = type;
      let typeTranslation = this.organizationMemberTypes.find((t: any) => t.value.toLowerCase() === typeValue.toLowerCase());
      // console.log(typeTranslation?.translation);
      type = {
        value: typeValue,
        translation: typeTranslation?.translation
      }
      return type;
    });
  }

  startEditing() {
    this.originalParkingData = JSON.parse(JSON.stringify(this.parkingData));
    this.parkingData.forEach(parking => parking.isEditing = true);
    this.isEditing = true;
    this.hasChanges = false;
  }

  cancelEditing() {
    this.parkingData = this.originalParkingData;
    this.isEditing = false;
    this.hasChanges = false;
  }

  onPermissionChange(parking: any, permissionType: string, value: boolean) {
    if (!parking.originalAccess) {
      parking.originalAccess = { ...parking.access };
    }
    parking.access[permissionType] = value;
    this.checkForChanges();
  }

  checkForChanges() {
    this.hasChanges = this.parkingData.some(parking => {
      if (!parking.originalAccess) return false;
      return parking.access.subscription !== parking.originalAccess.subscription ||
        parking.access.vehicle !== parking.originalAccess.vehicle ||
        parking.access.maintenance !== parking.originalAccess.maintenance;
    });
  }

  async savePermissions() {
    try {
      const apiCalls = this.parkingData
        .filter(parking => parking.originalAccess)
        .map(parking => {
          let accessLevel = 0;
          if (parking.access.subscription) accessLevel |= this.parkingAccessLevel.Subscription;
          if (parking.access.vehicle) accessLevel |= this.parkingAccessLevel.Vehicle;
          if (parking.access.maintenance) accessLevel |= this.parkingAccessLevel.Maintenance;

          const payload = {
            Id: parking?.parkingAccessId,
            OrganizationId: this.selectedOrg?.id,
            ParkingId: parking?.parkingId,
            AccessLevel: accessLevel
          };

          return this.backOfficeService.createOrUpdateOrganizationParkingAccess(
            this.organization.organizationId,
            payload
          ).pipe(
            takeUntil(this.componentDestroyed$),
            map(response => {
              const updatedParking = this.parkingData.find(p => p.parkingId === parking.parkingId);
              if (updatedParking) {
                updatedParking.accessLevel = response.accessLevel;
                updatedParking.parkingAccessId = response.id;

                updatedParking.access = {
                  subscription: (response.accessLevel & this.parkingAccessLevel.Subscription) === this.parkingAccessLevel.Subscription,
                  vehicle: (response.accessLevel & this.parkingAccessLevel.Vehicle) === this.parkingAccessLevel.Vehicle,
                  maintenance: (response.accessLevel & this.parkingAccessLevel.Maintenance) === this.parkingAccessLevel.Maintenance
                };

                this.updateOrganizationParkingAccess(updatedParking);
              }
              return { success: true, result: response, parkingId: parking.parkingId };
            }),
            catchError(error => {
              console.error(`Error updating parking ${parking.parkingId}:`, error);
              this.setAdminParkingAccess(this.selectedOrg);

              return of({
                success: false,
                error,
                parkingId: parking.parkingId,
                parkingName: parking.parkingName
              });
            })
          );
        });

      if (apiCalls.length === 0) {
        this.resetEditingState();
        return;
      }

      // Execute all API calls in parallel
      forkJoin(apiCalls).subscribe({
        next: (results) => {
          const failedUpdates = results.filter(r => !r.success);

          if (failedUpdates.length > 0) {
            console.error('Some parking permissions failed to update:', failedUpdates);
            // Show error notification for failed updates
            // this.snackBar.open(
            //   `Failed to update ${failedUpdates.length} parking permissions. Check console for details.`,
            //   'Close',
            //   { duration: 5000 }
            // );
          } else {
            // All updates successful
            // this.snackBar.open('All permissions updated successfully', 'Close', { duration: 3000 });
          }

          this.resetEditingState();
        },
        error: (error) => {
          console.error('Error updating parking permissions:', error);
          // this.snackBar.open('Error updating permissions', 'Close', { duration: 3000 });
          this.resetEditingState();
        }
      });

    } catch (error) {
      console.error('Failed to save permissions:', error);
      // this.snackBar.open('Failed to update permissions', 'Close', { duration: 3000 });
      this.resetEditingState();
    }
  }

  saveShuttleBookingAllowedUntil() {
    const selectedDate = this.selectedOrg?.shuttleBookingAllowedUntil;
    const payload = {
      Id: this.selectedOrg?.id,
      Name: this.selectedOrg?.name,
      Type: this.selectedOrg?.type,
      ParentId: this.selectedOrg?.parentId,
      ShuttleBookingAllowedUntil: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null
    };

    this.backOfficeService.createOrUpdateOrganization(this.selectedOrg?.id, payload)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (response) => {
          this.selectedOrg.shuttleBookingAllowedUntil = response.shuttleBookingAllowedUntil
            ? moment(response.shuttleBookingAllowedUntil)
            : null;
        },
        error: (error) => {
          console.error('Error updating shuttle booking allowed until:', error);
          this.snackBar.open(
            error.status === 403
              ? 'You do not have permission to update this organization'
              : 'Failed to save Shuttle Booking Allowed Until',
            'Close',
            { duration: 3000 }
          );
        }
      });
  }

  private resetEditingState() {
    // Reset editing state
    this.parkingData.forEach(parking => {
      parking.isEditing = false;
      parking.originalAccess = undefined;
    });
    this.isEditing = false;
    this.hasChanges = false;
  }

  private updateOrganizationParkingAccess(updatedParking: any) {
    // Update in the selected organization
    if (this.selectedOrg?.parkingAccess) {
      const existingAccess = this.selectedOrg.parkingAccess.find(
        (pa: any) => pa.parkingId === updatedParking.parkingId
      );

      if (existingAccess) {
        // Update existing access
        existingAccess.accessLevel = updatedParking.accessLevel;
        existingAccess.id = updatedParking.parkingAccessId;
      } else {
        // Add new access
        this.selectedOrg.parkingAccess.push({
          id: updatedParking.parkingAccessId,
          parkingId: updatedParking.parkingId,
          accessLevel: updatedParking.accessLevel
        });
      }
    }

    // Update in the organizations list
    const orgIndex = this.organizations.findIndex(org => org.id === this.selectedOrg?.id);
    if (orgIndex !== -1) {
      const org = this.organizations[orgIndex];
      if (!org.parkingAccess) {
        org.parkingAccess = [];
      }

      const orgAccessIndex = org.parkingAccess.findIndex(
        (pa: any) => pa.parkingId === updatedParking.parkingId
      );

      if (orgAccessIndex !== -1) {
        // Update existing access
        org.parkingAccess[orgAccessIndex] = {
          ...org.parkingAccess[orgAccessIndex],
          accessLevel: updatedParking.accessLevel,
          id: updatedParking.parkingAccessId
        };
      } else {
        // Add new access
        org.parkingAccess.push({
          id: updatedParking.parkingAccessId,
          parkingId: updatedParking.parkingId,
          accessLevel: updatedParking.accessLevel
        });
      }

      // Update the organizations array to trigger change detection
      this.organizations = [...this.organizations];
    }

    // Update the selectedOrg reference to trigger change detection
    this.selectedOrg = { ...this.selectedOrg };
  }
}
